import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { colors } from './theme';
import { getRmaCustomerDisplayState, countActionRequired } from './utils/rmaCustomerStatus';
import LoadingSpinner from './components/LoadingSpinner';
import { getWarrantyStatus, getWarrantyColor } from './warrantyUtils';
import CustomerRmaModalPending from './components/CustomerRmaModalPending.jsx';
import CustomerRmaModalAssigned from './components/CustomerRmaModalAssigned.jsx';
import CustomerRmaModalReceived from './components/CustomerRmaModalReceived.jsx';
import CustomerRmaModalRepairing from './components/CustomerRmaModalRepairing.jsx';
import CustomerRmaModalReady from './components/CustomerRmaModalReady.jsx';
import RmaModalShipped from './components/RmaModalShipped.jsx';
import CustomerRmaModalClosedCase from './components/CustomerRmaModalClosedCase.jsx';
import ArchivedRmaList from './components/ArchivedRmaList.jsx';
import OrgRmaTrendChart from './components/OrgRmaTrendChart.jsx';
import ProductThumb from './components/ProductThumb';

const API_BASE = process.env.REACT_APP_API_URL;



const STATUS_ALIASES = {
  Pending: 'pending',
  Initiated: 'pending',
  Assigned: 'assigned',
  Received: 'received',
  'Awaiting Repair Start': 'awaiting_repair',
  Assessment: 'awaiting_repair',
  'Repair In Progress': 'repairing',
  Repairing: 'repairing',
  Ready: 'awaiting_shipping',
  'Repair Finished': 'awaiting_shipping',
  'Repair Finished (Back in Stock/Refurbished)': 'awaiting_shipping',
  'Awaiting Shipping Details': 'awaiting_shipping',
  'Awaiting Dispatch Confirmation': 'awaiting_shipping',
  Shipped: 'shipped',
  ArchivedCaseClosed: 'archived',
  Closed: 'archived',
  'Repair Cancelled by Customer': 'canceled_by_customer',
  cancelled: 'archived',
  canceled_by_customer: 'canceled_by_customer',
  closed_case: 'closed_case'
};

const normalizeStatus = status => {
  if (!status) return 'pending';
  const alias = STATUS_ALIASES[status];
  if (alias) return alias;
  const lowered = typeof status === 'string' ? status.toLowerCase() : status;
  return STATUS_ALIASES[lowered] || lowered || 'pending';
};

const normalizeRma = rma => rma ? { ...rma, status: normalizeStatus(rma.status) } : rma;

const MODAL_COMPONENTS = {
  pending: CustomerRmaModalPending,
  initiated: CustomerRmaModalPending,
  assigned: CustomerRmaModalAssigned,
  received: CustomerRmaModalReceived,
  awaiting_repair: CustomerRmaModalRepairing,
  repairing: CustomerRmaModalRepairing,
  awaiting_shipping: CustomerRmaModalReady,
  shipped: RmaModalShipped,
  archived: RmaModalShipped,
  canceled_by_customer: CustomerRmaModalRepairing,
  closed_case: CustomerRmaModalClosedCase
};

export default function OrgDevicesPage() {
  const { getToken, isSignedIn } = useAuth();
  const navigate = useNavigate();

  const [org, setOrg] = useState(null);
  const [devices, setDevices] = useState([]);
  const [rmas, setRmas] = useState([]);
  const [archivedRmas, setArchivedRmas] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [archivedError, setArchivedError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('devices');
  const [activeRma, setActiveRma] = useState(null);
  const [customerDecisionState, setCustomerDecisionState] = useState({});
  const [declinePathState, setDeclinePathState] = useState({});
  const [challengeSubmittingId] = useState(null);

  const load = useCallback(async () => {
    if (!isSignedIn) return;
    setLoading(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}` };
      const [devRes, rmaRes] = await Promise.all([
        fetch(`${API_BASE}/org/devices`, { headers }),
        fetch(`${API_BASE}/org/rmas`, { headers })
      ]);
      if (devRes.ok) {
        const data = await devRes.json();
        setOrg(data.org || null);
        setDevices(data.devices || []);
      }
      if (rmaRes.ok) {
        const data = await rmaRes.json();
        setRmas((data.rmas || []).map(normalizeRma).filter(r => r.status !== 'closed_case'));
      }
    } catch (err) {
      console.error('Failed to load org data', err);
    } finally {
      setLoading(false);
    }
  }, [isSignedIn, getToken]);

  useEffect(() => { load(); }, [load]);

  const loadArchived = useCallback(async () => {
    if (!isSignedIn) return;
    setArchivedLoading(true);
    setArchivedError(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/org/rmas/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setArchivedRmas(data.items || []);
      } else {
        throw new Error('Failed to load archived RMAs');
      }
    } catch (err) {
      setArchivedError(err);
    } finally {
      setArchivedLoading(false);
    }
  }, [isSignedIn, getToken]);

  useEffect(() => {
    if (view === 'archive') {
      loadArchived();
    }
  }, [view, loadArchived]);

  const handleRmaUpdate = useCallback((updated, opts = {}) => {
    if (!updated || typeof updated !== 'object') {
      setActiveRma(null);
      return;
    }
    if (updated.archived) {
      setRmas(prev => prev.filter(r => r._id !== activeRma?._id));
      loadArchived();
      if (!opts?.keepOpen) {
        setActiveRma(null);
      }
      return;
    }
    const norm = normalizeRma(updated);
    setRmas(prev => prev.map(r => r._id === norm._id ? norm : r));
    if (opts?.keepOpen) {
      setActiveRma(norm);
    } else {
      setActiveRma(null);
    }
  }, [activeRma, loadArchived]);

  const handleCustomerDecision = useCallback(async (rma, decision) => {
    if (!rma?._id || !decision) return;
    const id = rma._id;
    setCustomerDecisionState(prev => ({ ...prev, [id]: { loading: true, error: '' } }));
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${id}/customer-decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ decision })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed');
      const norm = normalizeRma(data.rma || data);
      setRmas(prev => prev.map(r => r._id === norm._id ? norm : r));
      setActiveRma(norm);
      setCustomerDecisionState(prev => ({ ...prev, [id]: { loading: false, error: '' } }));
    } catch (err) {
      setCustomerDecisionState(prev => ({ ...prev, [id]: { loading: false, error: err.message || 'Failed' } }));
    }
  }, [getToken]);

  const handleDeclinePath = useCallback(async (rma, choice) => {
    if (!rma?._id || !choice) return;
    const id = rma._id;
    setDeclinePathState(prev => ({ ...prev, [id]: { loading: true, error: '' } }));
    try {
      const token = await getToken();
      const slugMap = { return_as_is: 'return', retain_and_close: 'close' };
      const slug = slugMap[choice];
      if (!slug) throw new Error('Unknown choice');
      const body = choice === 'return_as_is' ? { shippingCostAck: true } : {};
      const res = await fetch(`${API_BASE}/rma/${id}/decline-path/${slug}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Failed');
      const norm = normalizeRma(data.rma || data);
      setRmas(prev => prev.map(r => r._id === norm._id ? norm : r));
      setActiveRma(norm);
      setDeclinePathState(prev => ({ ...prev, [id]: { loading: false, error: '' } }));
    } catch (err) {
      setDeclinePathState(prev => ({ ...prev, [id]: { loading: false, error: err.message || 'Failed' } }));
    }
  }, [getToken]);

  const openChallengeModal = useCallback((rma, opts) => {
    if (opts?.updatedRma) {
      const norm = normalizeRma(opts.updatedRma);
      setRmas(prev => prev.map(r => r._id === norm._id ? norm : r));
      setActiveRma(norm);
    }
  }, []);

  const handleShipmentSaved = useCallback(updated => {
    if (!updated || typeof updated !== 'object') return;
    const norm = normalizeRma(updated);
    setRmas(prev => prev.map(r => r._id === norm._id ? norm : r));
    setActiveRma(norm);
  }, []);

  const handleModalClose = useCallback(() => setActiveRma(null), []);

  // Build trackingActions for the assigned modal
  const buildTrackingActions = useCallback((rma) => ({
    canSubmit: true,
    submit: async (submission) => {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/rma/${rma._id}/customer-shipment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          trackingNumber: submission.trackingNumber,
          carrier: submission.carrier,
          comment: submission.comment
        })
      });
      if (!res.ok) {
        let message = 'Failed to submit tracking.';
        try {
          const payload = await res.json();
          if (payload?.error) message = payload.error;
          else if (payload?.message) message = payload.message;
        } catch (_) {}
        throw new Error(message);
      }
      return await res.json();
    }
  }), [getToken]);

  if (!isSignedIn) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: colors.textMuted }}>
        Please sign in to view your company's devices.
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (!org) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <p style={{ color: colors.textMuted, marginBottom: '1rem' }}>
          You haven't joined a company yet.
        </p>
        <button className="ni-button ni-button--primary" onClick={() => navigate('/')}>
          Go to home
        </button>
      </div>
    );
  }

  const modalStatus = activeRma ? activeRma.status : null;
  const ModalComponent = modalStatus ? MODAL_COMPONENTS[modalStatus] : null;
  const modalExtraProps = {};
  if (modalStatus === 'assigned' && activeRma) {
    modalExtraProps.trackingActions = buildTrackingActions(activeRma);
    modalExtraProps.onShipmentSaved = handleShipmentSaved;
  }
  if (modalStatus === 'repairing' && activeRma) {
    modalExtraProps.onCustomerDecision = handleCustomerDecision;
    modalExtraProps.customerDecisionState = customerDecisionState[activeRma._id];
    modalExtraProps.onChallenge = openChallengeModal;
    modalExtraProps.challengeSubmittingId = challengeSubmittingId;
    modalExtraProps.onDeclinePath = handleDeclinePath;
    modalExtraProps.declinePathState = declinePathState[activeRma._id];
  }

  const actionNeededCount = countActionRequired(rmas);

  return (
    <div style={{ minHeight: '100vh', background: colors.bg || '#0b0f0d', padding: '0' }}>
      {/* Header */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        padding: '1rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <img
            src="/wave-configurator-app/inspiration/Horizontal logo SVG/SVG/North-Invent-neg-green-Long.svg"
            alt="North Invent"
            style={{ height: '26px', width: 'auto' }}
          />
          <div style={{ borderLeft: '1px solid rgba(255,255,255,0.12)', paddingLeft: '1rem' }}>
            <div style={{ color: colors.textMuted, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Company</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ color: colors.textStrong, fontWeight: 700, fontSize: '1rem' }}>{org.name}</span>
              {org.verified && (
                <span title="Verified company" style={{
                  background: 'rgba(70,187,137,0.15)',
                  color: '#46bb89',
                  border: '1px solid rgba(70,187,137,0.3)',
                  borderRadius: '999px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  padding: '0.1rem 0.5rem',
                  letterSpacing: '0.04em'
                }}>✓ Verified</span>
              )}
            </div>
          </div>
        </div>
        <button
          className="ni-button ni-button--ghost"
          style={{ fontSize: '0.85rem' }}
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
      </div>

      {/* Tab nav */}
      <div style={{ padding: '1rem 1.5rem 0', display: 'flex', gap: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        {[
          { key: 'devices', label: `Devices (${devices.length})` },
          {
            key: 'rmas', label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                RMA Cases ({rmas.length})
                {actionNeededCount > 0 && (
                  <span style={{
                    background: '#60a5fa',
                    color: '#0b1526',
                    borderRadius: '999px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '0.1rem 0.45rem',
                    lineHeight: 1.4
                  }}>{actionNeededCount}</span>
                )}
              </span>
            )
          },
          { key: 'archive', label: 'Archive' },
          { key: 'analytics', label: 'Analytics' }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setView(tab.key)}
            style={{
              background: 'none',
              border: 'none',
              borderBottom: view === tab.key ? '2px solid #46bb89' : '2px solid transparent',
              color: view === tab.key ? colors.textStrong : colors.textMuted,
              fontWeight: view === tab.key ? 700 : 400,
              padding: '0.6rem 1rem',
              cursor: 'pointer',
              fontSize: '0.9rem',
              marginBottom: '-1px',
              display: 'flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={{ padding: '1.5rem' }}>
        {view === 'analytics' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <OrgRmaTrendChart />
          </div>
        ) : view === 'archive' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
              <h2 style={{ margin: 0, color: colors.textStrong, fontFamily: "'Barlow Condensed', 'Barlow', sans-serif", fontSize: '2rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Archived RMAs
              </h2>
            </div>
            <ArchivedRmaList
              items={archivedRmas}
              isLoading={archivedLoading}
              error={archivedError}
              onRetry={loadArchived}
              emptyMessage="No archived RMAs for your company's devices."
            />
          </div>
        ) : view === 'devices' ? (
          devices.length === 0 ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: '3rem 0' }}>
              <p>No devices claimed yet.</p>
              <p style={{ fontSize: '0.85rem' }}>
                Scan a device's NFC tag or open a device link and claim it to add it here.
              </p>
            </div>
          ) : (
            <div className="ni-device-grid">
              {devices.map(device => {
                const status = getWarrantyStatus(device.scandate);
                const color = getWarrantyColor(status);
                return (
                  <Link
                    key={device._id}
                    to={`/device/${device._id}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div className="ni-device-card">
                      <span style={{
                        position: 'absolute', top: '0.6rem', right: '0.6rem',
                        width: '8px', height: '8px', borderRadius: '50%',
                        backgroundColor: color, display: 'block'
                      }} />
                      <div className="ni-device-card__media">
                        <ProductThumb
                          productNumber={device.productNumber}
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                      </div>
                      <div className="ni-device-card__body">
                        <strong className="ni-device-card__title">{device.productNumber}</strong>
                        <p className="ni-device-card__meta">Service Tag: {device.serviceTagNumber ?? 'N/A'}</p>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )
        ) : (
          rmas.length === 0 ? (
            <div style={{ textAlign: 'center', color: colors.textMuted, padding: '3rem 0' }}>
              No active RMA cases for your company's devices.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {rmas.map(rma => {
                const ds = getRmaCustomerDisplayState(rma);
                const hoverBg = ds.isActionRequired ? 'rgba(59,130,246,0.1)' : ds.isWaitingOnNI ? 'rgba(70,187,137,0.1)' : 'rgba(255,255,255,0.07)';
                return (
                  <div
                    key={rma._id}
                    onClick={() => setActiveRma(rma)}
                    style={{
                      background: ds.cardBg,
                      border: ds.cardBorder,
                      borderRadius: '10px',
                      padding: '1rem 1.25rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '0.75rem',
                      cursor: 'pointer',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
                    onMouseLeave={e => { e.currentTarget.style.background = ds.cardBg; }}
                  >
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ color: colors.textStrong, fontWeight: 600, fontSize: '0.95rem' }}>
                          {rma.rmaId}
                        </span>
                        {ds.badgeText && (
                          <span style={{
                            background: ds.badgeBg,
                            color: ds.badgeColor,
                            border: `1px solid ${ds.badgeBorder}`,
                            borderRadius: '999px',
                            fontSize: '0.7rem',
                            fontWeight: ds.isActionRequired ? 700 : 600,
                            padding: '0.1rem 0.5rem'
                          }}>{ds.badgeText}</span>
                        )}
                      </div>
                      <div style={{ color: colors.textMuted, fontSize: '0.82rem', marginTop: '0.2rem' }}>
                        Service tag: {rma.serialNumber}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{
                        background: `${ds.statusColor}22`,
                        color: ds.statusColor,
                        border: `1px solid ${ds.statusColor}44`,
                        borderRadius: '999px',
                        padding: '0.25rem 0.8rem',
                        fontSize: '0.8rem',
                        fontWeight: 600
                      }}>
                        {ds.statusLabel}
                      </span>
                      <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>›</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {ModalComponent && activeRma && (
        <ModalComponent
          rma={activeRma}
          onClose={handleModalClose}
          onSave={handleRmaUpdate}
          {...modalExtraProps}
        />
      )}
    </div>
  );
}
